import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import { toast } from "@/lib/use-toast";
import type { PhlebotomyOrder, PhlebotomyOrderCreatePayload, PhlebotomyOrderMarkCompletedPayload } from "@/types/phlebotomy-order";
import { createPhlebotomyOrder, fetchPhlebotomyOrderPreviewData, fetchPhlebotomyOrders, markPhlebotomyOrderCompleted } from "./phlebotomy-orders";
import { documentQueryKeys } from "@/api/provider/user/patient/document-query";
export const phlebotomyOrderPreviewQueryKeys = {
  all: ["phlebotomy-order", "preview-data"] as const,
  details: (patientId: string, orderingProviderId: string | undefined) => [...phlebotomyOrderPreviewQueryKeys.all, patientId, orderingProviderId ?? ""] as const
};

/**
 * React Query options for the derived phlebotomy-order preview fields
 * (ordering-site location, clinic fax) the compose form can't safely resolve
 * itself.
 *
 * Both the Preview and Create handlers resolve these via
 * `queryClient.fetchQuery(...)` so they share one code path and cache.
 * `staleTime: 0` forces a refetch on every resolve so the previewed PDF and
 * the bytes uploaded on create always reflect the *current* chart state
 * (location can change while the form is open) rather than a stale cached
 * resolution.
 */
export const phlebotomyOrderPreviewQueryOptions = {
  details: (patientId: string, orderingProviderId: string | undefined) => ({
    queryKey: phlebotomyOrderPreviewQueryKeys.details(patientId, orderingProviderId),
    // `enabled` gates useQuery callers; the compose handlers guard
    // `orderingProviderId` before calling `fetchQuery` (which ignores
    // `enabled`), so it's always defined when the queryFn runs.
    queryFn: () => fetchPhlebotomyOrderPreviewData(patientId, orderingProviderId as string),
    enabled: Boolean(orderingProviderId),
    staleTime: 0
  })
};
export const phlebotomyOrderQueryKeys = {
  all: ["phlebotomy-order"] as const,
  list: (patientId: string) => [...phlebotomyOrderQueryKeys.all, "list", patientId] as const
};
export const phlebotomyOrderQueryOptions = {
  // Backs the Sent / Completed status chips on the Documents tab, joined to
  // document rows by documentId.
  list: (patientId: string) => ({
    queryKey: phlebotomyOrderQueryKeys.list(patientId),
    queryFn: () => fetchPhlebotomyOrders(patientId),
    select: (data: PhlebotomyOrder[] | null) => data ?? []
  })
};

/** `enabled` defaults to true but should be passed false where the feature is
 *  gated off — the Documents tab renders on every patient chart for every
 *  tenant, and only phlebotomy-order tenants have anything to fetch. */
export function usePhlebotomyOrders(patientId, t0) {
  const $ = _c(5);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== patientId) {
    t1 = phlebotomyOrderQueryOptions.list(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== enabled || $[3] !== t1) {
    t2 = {
      ...t1,
      enabled
    };
    $[2] = enabled;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useQuery(t2);
}
export function useCreatePhlebotomyOrder() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp,
      onSuccess: _temp2,
      onError: _temp3
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp3(err) {
  toast({
    title: "Failed to create phlebotomy order",
    description: err instanceof Error ? err.message : "Could not create the order",
    variant: "error"
  });
}
function _temp2(order) {
  if (!order) {
    return;
  }
  queryClient.invalidateQueries({
    queryKey: documentQueryKeys.list(order.patientId)
  });
  queryClient.invalidateQueries({
    queryKey: phlebotomyOrderQueryKeys.list(order.patientId)
  });
  toast({
    title: "Phlebotomy order created",
    description: "The order is ready to send.",
    variant: "success"
  });
}
function _temp(t0) {
  const {
    data,
    pdf
  } = t0;
  return createPhlebotomyOrder(data, pdf);
}
export function useMarkPhlebotomyOrderCompleted() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp4,
      onSuccess: _temp5,
      onError: _temp6
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp6(err) {
  toast({
    title: "Failed to update completion status",
    description: err instanceof Error ? err.message : "Could not update the order",
    variant: "error"
  });
}
function _temp5(order) {
  if (!order) {
    return;
  }
  queryClient.invalidateQueries({
    queryKey: phlebotomyOrderQueryKeys.list(order.patientId)
  });
}
function _temp4(t0) {
  const {
    orderId,
    data
  } = t0;
  return markPhlebotomyOrderCompleted(orderId, data);
}